import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../../components/LoadingScreen';
import supabase from '../../utils/supabaseClient';

const UploadPitchDeckPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingDeck, setExistingDeck] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [deckName, setDeckName] = useState('');
  
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch product data
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
        
        if (productError) throw productError;
        setDeckName(productData.name + ' Pitch Deck');
        
        // Check if pitch deck already exists
        const { data: existingDeckData, error: deckError } = await supabase
          .from('pitch_decks')
          .select('*')
          .eq('product_id', id)
          .maybeSingle();
        
        if (!deckError && existingDeckData) {
          setExistingDeck(existingDeckData);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (user && id) {
      fetchData();
    }
  }, [user, id]);
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop().toLowerCase();
      
      // Validate file type - only accept PDF files
      if (fileExt !== 'pdf') {
        setError('Please upload a PDF file (.pdf)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File is too large. Maximum size is 10MB.');
        return;
      }
      
      setUploadedFile(file);
      setError(null);
    }
  };
  
  const uploadPitchDeck = async () => {
    if (!uploadedFile) {
      setError('Please select a file to upload');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      // Check if existing pitch deck requires confirmation
      if (existingDeck && !success) {
        if (!window.confirm('A pitch deck already exists for this product. Uploading a new one will replace the existing deck. Continue?')) {
          setUploading(false);
          return;
        }
      }
      
      // Upload file to Supabase Storage
      const fileExt = uploadedFile.name.split('.').pop().toLowerCase();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = fileName;
      
      const { error: uploadError } = await supabase.storage
        .from('pitch_decks')
        .upload(filePath, uploadedFile);
      
      if (uploadError) throw uploadError;
      
      // Get public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('pitch_decks')
        .getPublicUrl(filePath);
      
      if (!publicUrl) {
        throw new Error('Failed to get public URL for uploaded file');
      }
      
      // Save or update pitch deck in the database
      if (existingDeck) {
        // Update existing pitch deck
        const { error: updateError } = await supabase
          .from('pitch_decks')
          .update({
            name: deckName,
            is_uploaded: true,
            file_url: publicUrl,
            file_type: fileExt,
            updated_at: new Date()
          })
          .eq('id', existingDeck.id);
          
        if (updateError) throw updateError;
      } else {
        // Create new pitch deck
        const { error: insertError } = await supabase
          .from('pitch_decks')
          .insert({
            product_id: id,
            user_id: user.id,
            name: deckName,
            is_uploaded: true,
            file_url: publicUrl,
            file_type: fileExt
          })
          .select('id')
          .single();
          
        if (insertError) throw insertError;
      }
      
      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/products/${id}/pitchdeckview`);
      }, 1500);
      
    } catch (err) {
      console.error('Error uploading pitch deck:', err);
      setError(`Failed to upload pitch deck: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Upload Pitch Deck</h1>
          <Link 
            to={`/products/${id}`}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Product
          </Link>
        </div>
        
        {error && (
          <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}
        
        {success && (
          <div className="p-4 mb-6 bg-green-100 text-green-700 rounded-md" role="alert">
            <p className="font-medium">Success!</p>
            <p>Your pitch deck has been uploaded. Redirecting to view page...</p>
          </div>
        )}
        
        {existingDeck && !success && (
          <div className="p-4 mb-6 bg-yellow-100 text-yellow-800 rounded-md" role="alert">
            <p className="font-medium">Warning: Existing Pitch Deck</p>
            <p>This product already has a pitch deck. Uploading a new one will replace the existing deck.</p>
          </div>
        )}
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="mb-6">
            <div className="p-4 mb-6 bg-blue-50 text-blue-700 rounded-md" role="alert">
              <p className="font-medium">Important: PDF files only</p>
              <p>For improved viewing experience, pitch deck uploads are now limited to PDF format only.</p>
              <p className="mt-2 text-sm">Note: You can still create pitch decks using the in-app builder and download them as PPTX.</p>
            </div>
            
            <label htmlFor="deckName" className="block text-gray-700 font-medium mb-2">
              Pitch Deck Name
            </label>
            <input
              type="text"
              id="deckName"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Enter a name for your pitch deck"
              required
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Upload Pitch Deck File
            </label>
            <div className="mt-1 flex items-center flex-col space-y-4">
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf"
                onChange={handleFileChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              
              {uploadedFile && (
                <div className="flex items-center p-3 bg-gray-50 rounded-md w-full">
                  <svg className="w-8 h-8 text-primary-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 truncate">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {uploadedFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="ml-4 text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mt-1">
                Upload a PDF file (max size: 10MB)
              </p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            <Link
              to={`/products/${id}`}
              className="btn-secondary"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={uploadPitchDeck}
              disabled={uploading || !uploadedFile || success}
              className={`btn-primary ${(uploading || !uploadedFile || success) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </>
              ) : success ? (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Uploaded
                </>
              ) : (
                'Upload Pitch Deck'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPitchDeckPage; 